// 隐私政策 / Privacy Policy
//
// What we collect, why, who we share with, how long we keep it, your rights.
// Reachable without auth so Stripe Checkout can link to it. Honest about
// the third-party stack: Stripe (billing), DashScope/Aliyun (Wan video,
// Qwen LLM), Cloudflare (tunnel + DDoS), Gmail SMTP (verify codes).

import { Link } from 'react-router-dom';
import { LegalFooter } from '../components/LegalFooter';
import { TopBar } from '../components/TopBar';
import { PublicNavLinks } from '../components/PublicNavLinks';
import './Legal.css';

export function Privacy() {
  return (
    <div className="legal-page">
      <TopBar links={<PublicNavLinks />} />

      <main className="legal-shell">
        <h1>隐私政策 <span className="legal-en">/ Privacy Policy</span></h1>
        <p className="legal-meta">最后更新：2026-05-06 · v1.0</p>

        <section>
          <p>
            本政策说明 <strong>AutoLecture</strong>（"我们"）如何收集、使用、共享和保护您的
            个人信息。我们的设计原则：<strong>只收必要的信息，只用于运行服务，不卖数据。</strong>
          </p>
        </section>

        <section>
          <h2>1. 我们收集什么信息</h2>

          <h3>1.1 您主动提供的信息</h3>
          <ul>
            <li><strong>账户</strong>：电子邮箱（必需）、密码哈希（bcrypt，不可逆）、显示名（可选）</li>
            <li><strong>支付</strong>：付款时仅经手信用卡的<strong>最后四位 + 卡组织</strong>（由 Stripe 提供给我们用于显示），完整卡号 + CVV <strong>从不</strong>到达我们服务器，由 Stripe 直接处理与存储</li>
            <li><strong>项目内容</strong>：您上传或编辑的 .tex 源码、上传的素材（图片、音频、视频）、AI 生成的中间产物（Manim Python 代码、HTML 代码、TTS 音频、Whisper 转录）、编译产出的 mp4 视频</li>
          </ul>

          <h3>1.2 自动收集的信息</h3>
          <ul>
            <li><strong>使用数据</strong>：API 调用记录（端点、时间戳、消耗的 credits、成败状态）—— 用于计费、配额、问题诊断</li>
            <li><strong>请求元数据</strong>：IP 地址（由 Cloudflare 边缘节点收集，仅保留 7 天用于安全防护）、User-Agent、来源页面</li>
            <li><strong>编译日志</strong>：Manim / HTML / ffmpeg 的 stderr（仅在编译失败时保留 30 天用于您 review 错误）</li>
          </ul>

          <h3>1.3 我们不收集</h3>
          <ul>
            <li>不使用第三方分析（无 Google Analytics、无 Mixpanel、无 Hotjar）</li>
            <li>不使用第三方广告 cookie</li>
            <li>不收集精确地理位置（GPS）</li>
            <li>不收集您设备上的其他文件、通讯录、其他应用使用情况</li>
          </ul>
        </section>

        <section>
          <h2>2. 我们如何使用您的信息</h2>
          <ul>
            <li><strong>提供服务</strong>：执行您请求的编译、渲染、转录、订阅扣费</li>
            <li><strong>计费与防滥用</strong>：用使用记录扣 credits、检测异常用量</li>
            <li><strong>账户安全</strong>：登录验证、邮箱验证（6 位验证码）、检测可疑登录</li>
            <li><strong>必要通信</strong>：发送验证码、订阅续费收据、服务变更通知。我们 <strong>不会</strong> 用您的邮箱发送营销内容（除非您主动订阅 newsletter，目前未提供）</li>
            <li><strong>故障诊断</strong>：编译失败时分析日志、为您提供技术支持</li>
            <li><strong>法律合规</strong>：响应有效的法律请求（搜查令、税务申报、反洗钱）</li>
          </ul>
        </section>

        <section>
          <h2>3. 我们与谁共享</h2>

          <p>我们 <strong>不出售</strong> 您的数据。共享仅限以下场景的服务提供商：</p>

          <h3>Stripe（支付）</h3>
          <ul>
            <li>处理：完整支付卡数据、计费地址、订阅状态</li>
            <li>我们存：Stripe customer id（用于续费）、最后四位卡号、订阅状态</li>
            <li>Stripe 隐私政策：<a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">stripe.com/privacy</a></li>
          </ul>

          <h3>阿里云 DashScope（AI 视频 / LLM / TTS / ASR）</h3>
          <ul>
            <li>当您使用 Wan 视频、Qwen LLM、CosyVoice TTS、Paraformer ASR 等付费 AI 功能时，您的 prompt 文字、上传的图片、生成的中间结果会发送至 DashScope</li>
            <li>DashScope（阿里巴巴）按其隐私政策处理这些数据。我们已禁用其"用于训练"选项（在 API 调用层 set <code>train=false</code>）</li>
            <li>本地 Manim、本地 Whisper、本地 Qwen3-TTS 不向外发送数据</li>
            <li>DashScope 隐私政策：<a href="https://help.aliyun.com/document_detail/2858466.html" target="_blank" rel="noreferrer">help.aliyun.com/...</a></li>
          </ul>

          <h3>Cloudflare（域名 + DDoS 防护）</h3>
          <ul>
            <li>所有请求通过 Cloudflare tunnel 到达我们服务器</li>
            <li>Cloudflare 收集：IP、User-Agent、请求 URL，用于 DDoS 防护</li>
            <li>Cloudflare 隐私政策：<a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noreferrer">cloudflare.com/privacypolicy</a></li>
          </ul>

          <h3>Gmail SMTP（验证邮件）</h3>
          <ul>
            <li>注册验证、密码重置邮件经由 Gmail SMTP 发出</li>
            <li>不发送敏感内容（仅 6 位临时验证码）</li>
          </ul>

          <h3>法律强制披露</h3>
          <p>我们仅在收到合法、有管辖权、范围适当的法律请求时披露您的信息。除非法律禁止，我们会尝试事先通知您。</p>
        </section>

        <section>
          <h2>4. 数据保留时长</h2>
          <table className="account-table">
            <tbody>
              <tr><td>账户信息（邮箱、密码哈希）</td><td>账户存续期间</td></tr>
              <tr><td>项目 .tex / 素材 / mp4</td><td>账户存续期间，账户终止后 30 天</td></tr>
              <tr><td>缓存的渲染产物</td><td>项目存活期间，自动清理超过 90 天未访问的</td></tr>
              <tr><td>付款记录（订阅、topup）</td><td>7 年（财税法定）</td></tr>
              <tr><td>使用日志（UsageEvent）</td><td>13 个月，便于您查询年度账单</td></tr>
              <tr><td>IP 地址（Cloudflare 边缘）</td><td>7 天</td></tr>
              <tr><td>编译失败日志</td><td>30 天</td></tr>
              <tr><td>Whisper 转录 sidecar JSON</td><td>原音频删除后立即删除</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>5. 您的权利</h2>

          <h3>5.1 访问与导出</h3>
          <p>
            您可随时通过 Studio 的文件树<strong>导出</strong>所有 .tex / 素材 / mp4。所有项目内容
            按 ADR-006 永远免费导出。
          </p>

          <h3>5.2 更正</h3>
          <p>登录后在 Studio / Account 页可自助修改邮箱、密码、显示名。</p>

          <h3>5.3 删除（"被遗忘权"）</h3>
          <p>
            发邮件至 <a href="mailto:codescao7@gmail.com">codescao7@gmail.com</a>{' '}
            请求删除账户。我们会在 7 个工作日内删除您的：账户、所有项目、所有素材、所有缓存。
            付款记录会按法定保留期保留（脱敏后），无法立即删除。
          </p>

          <h3>5.4 限制处理与反对</h3>
          <p>您可要求我们停止特定的处理活动（例如不再使用您的项目缓存）。</p>

          <h3>5.5 数据可携带性</h3>
          <p>除自助导出，您可申请获取您账户下所有数据的机器可读副本（JSON + 原始文件）。</p>

          <h3>5.6 投诉</h3>
          <p>
            如您认为我们违反隐私法规，可向您所在司法辖区的数据保护机构投诉。中国大陆用户可向
            国家网信办或市场监管部门反映。
          </p>
        </section>

        <section>
          <h2>6. 数据安全</h2>
          <ul>
            <li>所有传输使用 <strong>HTTPS / TLS 1.3</strong>（Cloudflare 终结）</li>
            <li>密码使用 <strong>bcrypt</strong>（cost 12）哈希存储，不可逆</li>
            <li>JWT 用 <code>HS256 + 7 天过期</code>，签名密钥从不进 git</li>
            <li>Stripe / DashScope / SMTP 密钥通过环境变量或加密 SystemConfig 表存储，仅 admin 可读</li>
            <li>数据库（SQLite）位于私有服务器，不对外开放端口；定期本地备份</li>
            <li>定期检查依赖项漏洞（npm audit / pip-audit）</li>
          </ul>
          <p>
            我们承诺尽力保护您的数据，但<strong>不能 100% 保证</strong>不发生数据事故。
            如发生影响您的数据泄露，我们会在 72 小时内邮件通知您。
          </p>
        </section>

        <section>
          <h2>7. Cookie 与本地存储</h2>
          <p>我们使用以下浏览器存储：</p>
          <ul>
            <li><strong>localStorage</strong>：登录 JWT（key = <code>al_token</code>）+ 缓存的用户信息（key = <code>al_user</code>）。无 cookie</li>
            <li><strong>sessionStorage</strong>：未完成的 checkout 意图（用户登录后回到 Pricing 页继续付款）</li>
          </ul>
          <p>
            没有第三方追踪 cookie。Cloudflare 可能在边缘设置安全相关的 cookie（如{' '}
            <code>__cf_bm</code>），由 Cloudflare 管理。
          </p>
        </section>

        <section>
          <h2>8. 未成年人</h2>
          <p>
            本服务面向 16 周岁以上用户（或您所在司法辖区允许独立缔约的最低年龄）。
            如发现 16 岁以下用户在未经监护人同意下使用，请联系我们删除该账户。
          </p>
        </section>

        <section>
          <h2>9. 跨境数据传输</h2>
          <p>
            我们的服务器位于<strong>中国境内</strong>，但部分第三方服务（Stripe、Cloudflare）
            位于境外。这意味着您的部分数据（IP、付款信息）会跨境传输。
            我们与这些供应商签订了符合 <strong>GDPR / 中国《个人信息保护法》</strong>
            的数据处理协议（DPA / SCC）。
          </p>
        </section>

        <section>
          <h2>10. 政策变更</h2>
          <p>
            我们可能不时更新本政策。重大变更（影响您权利或扩展数据使用范围）将通过邮件
            <strong>提前 14 天</strong>告知。继续使用本服务即视为接受变更。
          </p>
        </section>

        <section>
          <h2>11. 联系我们</h2>
          <p>
            隐私问题、删除请求、数据导出申请请发邮件至：<br />
            <a href="mailto:codescao7@gmail.com">codescao7@gmail.com</a>
          </p>
          <p>
            另请参考 <Link to="/terms">服务条款</Link>。
          </p>
        </section>

        <p className="legal-foot muted small">
          本政策的中文版为正式版本。
        </p>
      </main>

      <LegalFooter />
    </div>
  );
}
